import { useEffect, useMemo, useState } from 'react';

import {

  Alert,

  Box,

  Button,

  Chip,

  Dialog,

  DialogActions,

  DialogContent,

  DialogTitle,

  MenuItem,

  Stack,

  Tab,

  Tabs,

  TextField,

  Typography,

} from '@mui/material';

import api from '@/services/api';

import { RefereeMemberSearch, formatMainAppUser } from '@/components/review/MainAppUserPicker';

import { canSetRemarkDeadline, sharePermissionLabel } from '@/components/review/sharePermission';

import ShareEditDialog from '@/components/review/ShareEditDialog';
import type { OfficialSourceOption } from '@/components/review/officialSource';

import { useAuth } from '@/hooks/useAuth';

import { buildOfficialSourceMemberOptions, type GroupMemberRow } from '@/utils/groupMembers';

import type { MainAppUser, ShareGroup, SessionShare } from '@/types';



interface Props {

  open: boolean;

  sessionId: string;

  shares: SessionShare[];

  ownerUserId?: number;

  canReleaseOfficial?: boolean;

  onClose: () => void;

  onUpdated: () => void;

}



function shareUserLabel(share: SessionShare): string {

  if (share.target_user) return formatMainAppUser(share.target_user);

  if (share.target_user_id) return `#${share.target_user_id}`;

  return '—';

}



function shareGroupLabel(share: SessionShare): string {

  if (share.target_group_name) return share.target_group_name;

  if (share.target_group_id) return `群組 ${share.target_group_id.slice(0, 8)}…`;

  return '—';

}



export default function ShareSessionDialog({

  open,

  sessionId,

  shares,

  ownerUserId,

  canReleaseOfficial,

  onClose,

  onUpdated,

}: Props) {

  const { user } = useAuth();

  const [tab, setTab] = useState(0);

  const [selectedUsers, setSelectedUsers] = useState<MainAppUser[]>([]);

  const [groups, setGroups] = useState<ShareGroup[]>([]);

  const [selectedGroupId, setSelectedGroupId] = useState('');

  const [permission, setPermission] = useState<'participant' | 'viewer'>('participant');

  const [message, setMessage] = useState('');

  const [dueAt, setDueAt] = useState('');

  const [loading, setLoading] = useState(false);

  const [notice, setNotice] = useState<string | null>(null);

  const [editingShare, setEditingShare] = useState<SessionShare | null>(null);

  const [editOfficialSourceOptions, setEditOfficialSourceOptions] = useState<OfficialSourceOption[]>([]);



  const sharedUserIds = useMemo(

    () =>

      shares

        .filter((s) => s.share_type === 'user' && s.target_user_id != null)

        .map((s) => s.target_user_id as number),

    [shares]

  );



  const sharedGroupIds = useMemo(

    () =>

      shares

        .filter((s) => s.share_type === 'group' && s.target_group_id)

        .map((s) => s.target_group_id as string),

    [shares]

  );



  const excludeUserIds = useMemo(() => {

    const ids = [...sharedUserIds];

    if (ownerUserId) ids.push(ownerUserId);

    return ids;

  }, [sharedUserIds, ownerUserId]);



  const showDeadline = canSetRemarkDeadline(permission);



  useEffect(() => {

    if (!open) return;

    setNotice(null);

    api.get<{ groups: ShareGroup[] }>('/groups').then((r) => setGroups(r.data.groups)).catch(() => {});

  }, [open]);



  useEffect(() => {

    if (!editingShare || editingShare.share_type !== 'group' || !editingShare.target_group_id) {

      setEditOfficialSourceOptions([]);

      return;

    }

    let cancelled = false;

    api

      .get<{ members: GroupMemberRow[] }>(`/groups/${editingShare.target_group_id}`)

      .then((r) => {

        if (cancelled) return;

        setEditOfficialSourceOptions(

          buildOfficialSourceMemberOptions(

            r.data.members ?? [],

            ownerUserId,

            (m) => m.full_name?.trim() || m.reference_no,

            user?.id

          )

        );

      })

      .catch(() => {

        if (!cancelled) setEditOfficialSourceOptions([]);

      });

    return () => {

      cancelled = true;

    };

  }, [editingShare, ownerUserId, user?.id]);



  const handleShare = async () => {

    setLoading(true);

    setNotice(null);

    try {

      if (tab === 0) {

        const res = await api.post<{ shares?: SessionShare[]; skipped?: number; message?: string }>(

          `/shares/session/${sessionId}`,

          {

            share_type: 'user',

            target_user_ids: selectedUsers.map((u) => u.id),

            permission,

            message: message || undefined,

            due_at: showDeadline && dueAt ? dueAt : undefined,

          }

        );

        if (res.data.message) setNotice(res.data.message);

        else if (res.data.skipped) {

          setNotice(`已分享 ${res.data.shares?.length ?? 0} 人，略過 ${res.data.skipped} 位已分享用戶`);

        }

        setSelectedUsers([]);

      } else {

        await api.post(`/shares/session/${sessionId}`, {

          share_type: 'group',

          target_group_id: selectedGroupId,

          permission,

          message: message || undefined,

          due_at: showDeadline && dueAt ? dueAt : undefined,

        });

        setSelectedGroupId('');

      }

      setMessage('');

      setDueAt('');

      onUpdated();

    } catch (err: unknown) {

      const msg =

        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||

        '分享失敗';

      setNotice(msg);

    } finally {

      setLoading(false);

    }

  };



  const handleRemoveShare = async (shareId: string) => {

    await api.delete(`/shares/${shareId}`);

    onUpdated();

  };



  const canShareUsers = tab === 0 && selectedUsers.length > 0;

  const canShareGroup = tab === 1 && !!selectedGroupId && !sharedGroupIds.includes(selectedGroupId);



  return (

    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">

      <DialogTitle>分享分析</DialogTitle>

      <DialogContent>

        {notice && (

          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice(null)}>

            {notice}

          </Alert>

        )}



        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>

          <Tab label="指定用戶" />

          <Tab label="指定群組" />

          <Tab label="已分享" />

        </Tabs>



        {tab === 0 && (

          <Stack spacing={2}>

            {selectedUsers.length > 0 && (

              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>

                {selectedUsers.map((u) => (

                  <Chip

                    key={u.id}

                    label={formatMainAppUser(u)}

                    size="small"

                    onDelete={() => setSelectedUsers((prev) => prev.filter((x) => x.id !== u.id))}

                  />

                ))}

              </Stack>

            )}

            <RefereeMemberSearch

              selected={selectedUsers}

              onSelectedChange={setSelectedUsers}

              excludeIds={excludeUserIds}

              addLabel="加入"

            />

            {sharedUserIds.length > 0 && (

              <Typography variant="caption" color="text.secondary">

                已分享用戶不會出現在列表中（共 {sharedUserIds.length} 人）

              </Typography>

            )}

          </Stack>

        )}



        {tab === 1 && (

          <Stack spacing={2}>

            <TextField select label="選擇群組" value={selectedGroupId} onChange={(e) => setSelectedGroupId(e.target.value)} fullWidth>

              <MenuItem value="">—</MenuItem>

              {groups.map((g) => {

                const already = sharedGroupIds.includes(g.id);

                return (

                  <MenuItem key={g.id} value={g.id} disabled={already}>

                    {g.name} ({g.member_count ?? 0} 人){already ? ' · 已分享' : ''}

                  </MenuItem>

                );

              })}

            </TextField>

            <Typography variant="caption" color="text.secondary">

              同一影片可分享至多個群組，各群組的回覆與發佈狀態互不干擾

            </Typography>

          </Stack>

        )}



        {tab === 2 && (

          <Stack spacing={1}>

            {shares.length === 0 ? (

              <Typography color="text.secondary">尚未分享</Typography>

            ) : (

              shares.map((s) => (

                <Box

                  key={s.id}

                  sx={{

                    display: 'flex',

                    justifyContent: 'space-between',

                    alignItems: 'center',

                    py: 1,

                    borderBottom: '1px solid',

                    borderColor: 'divider',

                    gap: 1,

                  }}

                >

                  <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>

                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">

                      <Typography variant="body2" fontWeight={700} noWrap>

                        {s.share_type === 'user' ? shareUserLabel(s) : shareGroupLabel(s)}

                      </Typography>

                      <Chip

                        size="small"

                        label={sharePermissionLabel(s.permission)}

                        variant="outlined"

                        color={s.permission === 'participant' ? 'secondary' : 'default'}

                        sx={{ height: 22 }}

                      />

                      {s.share_type === 'group' && s.permission === 'participant' && (
                        <Chip
                          size="small"
                          color={s.official_released_at ? 'success' : 'error'}
                          variant="outlined"
                          label={s.official_released_at ? '已發佈判決' : '未發佈判決'}
                          sx={{ height: 22 }}
                        />
                      )}

                    </Stack>

                    {s.due_at && s.permission === 'participant' && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        截止 {new Date(s.due_at).toLocaleString('zh-HK', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    )}

                    {s.message && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
                        {s.message}
                      </Typography>
                    )}

                  </Stack>

                  <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                    <Button size="small" variant="outlined" onClick={() => setEditingShare(s)}>
                      編輯
                    </Button>
                    <Button size="small" color="error" onClick={() => handleRemoveShare(s.id)}>
                      移除
                    </Button>
                  </Stack>

                </Box>

              ))

            )}

          </Stack>

        )}



        {tab < 2 && (

          <Stack spacing={2} sx={{ mt: 2 }}>

            <TextField

              select

              label="分享權限"

              value={permission}

              onChange={(e) => {

                const next = e.target.value as 'participant' | 'viewer';

                setPermission(next);

                if (!canSetRemarkDeadline(next)) setDueAt('');

              }}

              fullWidth

            >

              <MenuItem value="participant">{sharePermissionLabel('participant')}</MenuItem>

              <MenuItem value="viewer">{sharePermissionLabel('viewer')}</MenuItem>

            </TextField>

            {showDeadline && (

              <TextField

                label="備註截止時間"

                type="datetime-local"

                value={dueAt}

                onChange={(e) => setDueAt(e.target.value)}

                InputLabelProps={{ shrink: true }}

                fullWidth

              />

            )}

            <TextField label="附言" value={message} onChange={(e) => setMessage(e.target.value)} fullWidth multiline rows={2} />

          </Stack>

        )}

      </DialogContent>

      <DialogActions>

        <Button onClick={onClose}>關閉</Button>

        {tab < 2 && (

          <Button

            variant="contained"

            color="secondary"

            disabled={loading || (tab === 0 ? !canShareUsers : !canShareGroup)}

            onClick={handleShare}

          >

            {tab === 0 && selectedUsers.length > 1 ? `分享 ${selectedUsers.length} 人` : '分享'}

          </Button>

        )}

      </DialogActions>

      <ShareEditDialog
        open={!!editingShare}
        share={editingShare}
        targetLabel={
          editingShare
            ? editingShare.share_type === 'user'
              ? shareUserLabel(editingShare)
              : shareGroupLabel(editingShare)
            : ''
        }
        canReleaseOfficial={canReleaseOfficial}
        sessionOwnerUserId={ownerUserId}
        officialSourceOptions={editOfficialSourceOptions}
        onClose={() => setEditingShare(null)}
        onSaved={onUpdated}
      />

    </Dialog>

  );

}


